import { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";

/**
 * URL-driven "open record" state for list pages, e.g. /invoices/:invoiceId.
 * - selected: the record matching the URL id (always the latest copy from `items`)
 * - open(id): pushes the detail URL (Back closes it)
 * - step(id): switches to another record in place (replace — Back still returns to the list)
 * - close(): goes back to the list (history back when opened from the list, else replace)
 * - notFound: true briefly after a link pointed at a record that doesn't exist
 */
export function useRoutedSelection({ items = [], loading = false, paramName, basePath }) {
  const params = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const id = params[paramName] || null;

  const selected = id ? items.find((item) => item.id === id) || null : null;
  const [notFound, setNotFound] = useState(false);
  const seenIds = useRef(new Set());

  if (selected) seenIds.current.add(selected.id);

  useEffect(() => {
    if (!id || loading || selected) return;
    // A record that was open and has now disappeared was deleted — no "not found" notice
    if (!seenIds.current.has(id)) setNotFound(true);
    navigate(basePath, { replace: true });
  }, [id, loading, selected, basePath, navigate]);

  useEffect(() => {
    if (!notFound) return undefined;
    const timer = setTimeout(() => setNotFound(false), 4000);
    return () => clearTimeout(timer);
  }, [notFound]);

  const open = (recordId) => {
    setNotFound(false);
    navigate(`${basePath}/${recordId}`, { state: { fromList: true } });
  };

  const step = (recordId) => {
    navigate(`${basePath}/${recordId}`, { replace: true, state: location.state });
  };

  const close = () => {
    // Already back on the list (e.g. record deleted and redirected) — nothing to do
    if (!window.location.pathname.startsWith(`${basePath}/`)) return;
    if (location.state?.fromList) navigate(-1);
    else navigate(basePath, { replace: true });
  };

  return { selected, selectedId: id, open, step, close, notFound };
}

export default useRoutedSelection;
